import { Component, type ReactNode } from 'react';
import { ProteusDocumentRenderer, type ProteusDocumentRendererProps } from '@optiaxiom/proteus';

type ProteusElement = ProteusDocumentRendererProps['element'];

type Props = {
  /** The inline Proteus payload stored on the sticky (sticky.proteus_card). */
  card: Record<string, unknown>;
  /** Rendered if the payload is missing/invalid or the renderer throws. */
  fallback: ReactNode;
  /**
   * Controlled form state for the card's interactive fields (e.g. checklist
   * toggles), keyed by jsonpointer path. The renderer is a controlled form:
   * without `data` + `onDataChange` wired, a toggle's change is dropped and it
   * snaps back. The owner (StickyCard) persists this as the user's own state.
   */
  data: Record<string, unknown>;
  onDataChange: NonNullable<ProteusDocumentRendererProps['onDataChange']>;
};

/**
 * A genuine Proteus document carries a `$type` discriminator at its root (the
 * shape chat-frontend feeds the renderer). Anything else — most commonly a
 * TipTap/ProseMirror doc (`{ type: "doc", content: [...] }`) an LLM produced —
 * is NOT a Proteus card: `ProteusDocumentRenderer` silently no-ops on it
 * (renders nothing without throwing), so we treat it as "no card" and let the
 * text fallback (body_excerpt) render instead.
 */
function isProteusDocument(card: Record<string, unknown>): boolean {
  return typeof card['$type'] === 'string';
}

/**
 * Renders a sticky's inline Proteus card via @optiaxiom/proteus. The payload
 * is stored on the sticky doc (not fetched by URL like chat's ProteusMessage),
 * so we hand it straight to ProteusDocumentRenderer. `strict={false}` makes the
 * renderer no-op on a malformed document; the ErrorBoundary catches anything
 * that still throws and shows the text fallback (title + body excerpt).
 */
class ProteusErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export function ProteusStickyCard({ card, fallback, data, onDataChange }: Props) {
  if (!card || typeof card !== 'object' || !isProteusDocument(card)) {
    return <>{fallback}</>;
  }
  // The sticky already provides the card chrome (border, padding, sender chip,
  // title). Force the Proteus document to render `inline` so it doesn't draw a
  // second bordered shell — and so its own title/appName header is suppressed,
  // avoiding a duplicate title. (ProteusDocumentShell: inline ⇒ no border/bg/
  // padding and `!inline && element.title` / `!inline && element.appName`.)
  const inlineCard = { ...card, appearance: 'inline' };
  return (
    <ProteusErrorBoundary fallback={fallback}>
      <ProteusDocumentRenderer
        element={inlineCard as unknown as ProteusElement}
        data={data}
        onDataChange={onDataChange}
        strict={false}
      />
    </ProteusErrorBoundary>
  );
}
